import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Filter, ShoppingBag } from 'lucide-react';
import StoreLayout from '../../components/store/StoreLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { publicAPI } from '../../lib/api';
import { formatPrice, calculateSavingsPercent, debounce } from '../../lib/utils';
import { useCart } from '../../contexts/CartContext';
import { trackSearch } from '../../lib/tracking';
import { toast } from 'sonner';

const GENDERS = [
  { key: 'all', label: 'Tous' },
  { key: 'women', label: 'Femme' },
  { key: 'men', label: 'Homme' },
];

const STYLES = [
  { key: 'all', label: 'Tous les styles' },
  { key: 'chic', label: 'Chic' },
  { key: 'casual', label: 'Casual' },
  { key: 'glamour', label: 'Glamour' },
];

// Enhanced Look Card with hover effect
function LookCard({ look, index, onAddToCart }) {
  const [isHovered, setIsHovered] = useState(false);
  const savingsPercent = calculateSavingsPercent(
    look.original_price_cents, 
    look.bundle_price_cents
  );

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="group relative bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link 
        to={`/looks/${look.slug}`}
        className="block"
        data-testid={`look-card-${look.slug}`}
      >
        {/* Main Image Container */}
        <div className="relative aspect-[3/4] overflow-hidden bg-gradient-to-b from-gray-50 to-gray-100">
          <img
            src={look.images?.[0] || 'https://images.pexels.com/photos/8971312/pexels-photo-8971312.jpeg?w=800'}
            alt={look.name}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          
          {/* Savings Badge */}
          {savingsPercent > 0 && (
            <div className="absolute top-4 left-4 bg-black text-white px-3 py-1.5 text-sm font-medium rounded">
              -{savingsPercent}%
            </div>
          )}

          {/* Gender Badge */}
          <div className="absolute top-4 right-4">
            <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm text-foreground">
              {look.gender === 'men' ? 'Homme' : 'Femme'}
            </Badge>
          </div>

          {/* Hover Overlay with Product Details */}
          <AnimatePresence>
            {isHovered && look.products && look.products.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm flex flex-col justify-end p-4"
              >
                <p className="text-white/80 text-sm mb-2">Articles inclus :</p>
                <div className="space-y-2 max-h-[200px] overflow-auto">
                  {look.products.map((product) => (
                    <div 
                      key={product.id}
                      className="flex items-center gap-2 bg-white/10 rounded-lg p-2"
                    >
                      <div className="w-10 h-12 rounded overflow-hidden flex-shrink-0 bg-white">
                        <img 
                          src={product.images?.[0]} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {product.name}
                        </p>
                        <p className="text-white/70 text-xs">
                          {formatPrice(product.variants?.[0]?.price_cents || 0)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <Button 
                  className="w-full mt-3"
                  onClick={(e) => {
                    e.preventDefault();
                    onAddToCart(look);
                  }}
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Ajouter au panier
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Quick View Button (visible on hover) */}
          {!isHovered && (
            <div className="absolute inset-0 flex items-end justify-center pb-6 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="bg-white text-foreground px-6 py-2 text-sm font-medium transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 rounded shadow-lg">
                Voir le look
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-4 bg-white">
          {/* Style & Occasion Tags */}
          <div className="flex gap-2 mb-2">
            {look.style && (
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                {look.style}
              </span>
            )}
            {look.occasion && (
              <>
                <span className="text-muted-foreground/50">•</span>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">
                  {look.occasion}
                </span>
              </>
            )}
          </div>

          {/* Name */}
          <h3 className="font-display text-xl font-medium group-hover:underline underline-offset-4 decoration-1">
            {look.name}
          </h3>

          {/* Prices */}
          <div className="flex items-baseline gap-3 mt-2">
            <span className="text-lg font-semibold">
              {formatPrice(look.bundle_price_cents)}
            </span>
            {look.original_price_cents > look.bundle_price_cents && (
              <span className="text-sm text-muted-foreground line-through">
                {formatPrice(look.original_price_cents)}
              </span>
            )}
          </div>

          {/* Items count */}
          <p className="text-sm text-muted-foreground mt-1">
            {look.items?.length || 0} articles inclus
          </p>
        </div>
      </Link>
    </motion.article>
  );
}

export default function LooksPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addItem, openCart } = useCart();
  
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  
  const activeGender = searchParams.get('gender') || 'all';
  const activeStyle = searchParams.get('style') || 'all';
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (!searchQuery) {
      loadLooks();
    }
  }, [activeGender, activeStyle, page]);

  const loadLooks = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 12,
      };
      if (activeGender !== 'all') {
        params.gender = activeGender;
      }
      if (activeStyle !== 'all') {
        params.style = activeStyle;
      }
      
      const response = await publicAPI.getLooks(params);
      setLooks(response.data.items);
      setTotalPages(response.data.pages);
      setSearchResults(null);
    } catch (error) {
      console.error('Error loading looks:', error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (query.length < 2) {
        setSearchResults(null);
        setIsSearching(false);
        return;
      }
      
      setIsSearching(true);
      try {
        const response = await publicAPI.search(query);
        setSearchResults(response.data);
        trackSearch(query);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  const handleSearch = (value) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleFilterChange = (type, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      newParams.delete(type);
    } else {
      newParams.set(type, value);
    }
    setSearchParams(newParams);
    setPage(1);
  };

  const handleAddToCart = async (look) => {
    try {
      await addItem({
        look_id: look.id,
        quantity: 1,
        name: look.name
      });
      toast.success('Look ajouté au panier !');
      openCart();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const displayedLooks = searchResults?.looks || looks;

  return (
    <StoreLayout>
      <section className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="font-display text-4xl sm:text-5xl font-bold">
              {t('looks.title')}
            </h1>
            <p className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
              Découvrez nos looks complets, prêts à porter. 
              Chaque ensemble est soigneusement composé pour un style impeccable.
            </p>
          </motion.div>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-xl mx-auto mb-8"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Rechercher un look, un style, une occasion..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-12 pr-10 py-6 text-base rounded-full border-2 focus:border-foreground"
                data-testid="search-input"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            {searchResults && (
              <p className="text-sm text-muted-foreground mt-2 text-center">
                {searchResults.total} résultat(s) pour "{searchQuery}"
              </p>
            )}
          </motion.div>

          {/* Gender Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="flex justify-center mb-6"
          >
            <div className="inline-flex bg-muted rounded-full p-1">
              {GENDERS.map((gender) => (
                <button
                  key={gender.key}
                  onClick={() => handleFilterChange('gender', gender.key)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                    activeGender === gender.key
                      ? 'bg-foreground text-background shadow'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`gender-${gender.key}`}
                >
                  {gender.label}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Style Filters */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center gap-2 mb-12 flex-wrap"
          >
            {STYLES.map((style) => (
              <Button
                key={style.key}
                variant={activeStyle === style.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleFilterChange('style', style.key)}
                data-testid={`style-${style.key}`}
              >
                {style.label}
              </Button>
            ))}
          </motion.div>

          {/* Grid */}
          {loading || isSearching ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-muted rounded-lg" />
                  <div className="p-4">
                    <div className="h-4 bg-muted rounded w-1/3 mb-2" />
                    <div className="h-6 bg-muted rounded w-2/3 mb-2" />
                    <div className="h-4 bg-muted rounded w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : displayedLooks.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg">
                {searchQuery 
                  ? `Aucun résultat pour "${searchQuery}"`
                  : 'Aucun look trouvé pour ces filtres.'
                }
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => {
                  clearSearch();
                  handleFilterChange('gender', 'all');
                  handleFilterChange('style', 'all');
                }}
              >
                Voir tous les looks
              </Button>
            </div>
          ) : (
            <>
              <div 
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                data-testid="looks-grid"
              >
                {displayedLooks.map((look, index) => (
                  <LookCard 
                    key={look.id} 
                    look={look} 
                    index={index}
                    onAddToCart={handleAddToCart}
                  />
                ))}
              </div>

              {/* Pagination */}
              {!searchResults && totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-12">
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Précédent
                  </Button>
                  <span className="flex items-center px-4 text-sm text-muted-foreground">
                    Page {page} sur {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Suivant
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </StoreLayout>
  );
}
